import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiChevronDown } from 'react-icons/fi';

const Wrap = styled.div`
  position: relative;
  width: 100%;
  min-width: ${(p) => p.$minW || '200px'};
  max-width: ${(p) => p.$maxW || '280px'};
`;

const Trigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.55rem 0.65rem;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 0.88rem;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: rgba(79, 172, 254, 0.35);
  }

  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const TriggerText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 80;
  background: linear-gradient(180deg, #1a1a32 0%, #151528 100%);
  border: 1px solid rgba(79, 172, 254, 0.35);
  border-radius: 10px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.55);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: min(320px, 70vh);
`;

const SearchInput = styled.input`
  margin: 0.5rem;
  padding: 0.5rem 0.65rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 0.85rem;

  &::placeholder {
    color: #64748b;
  }

  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0.2rem 0 0.4rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const OptionRow = styled.li`
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  cursor: pointer;
  color: #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  background: ${(p) => (p.$highlight ? 'rgba(79, 172, 254, 0.22)' : 'transparent')};
  border-left: 3px solid ${(p) => (p.$highlight ? '#4facfe' : 'transparent')};

  &:hover {
    background: ${(p) => (p.$highlight ? 'rgba(79, 172, 254, 0.28)' : 'rgba(79, 172, 254, 0.12)')};
  }
`;

const SelectedMark = styled.span`
  font-size: 0.7rem;
  color: #4facfe;
  font-weight: 800;
  width: 1rem;
  flex-shrink: 0;
`;

/**
 * Dark-theme searchable dropdown for filter bars (Admin Assets, etc.).
 * @param {{ value: string, label: string }[]} options
 * @param {(value: string) => void} onChange
 */
export function SearchableFilterSelect({
  id,
  'aria-label': ariaLabel,
  /** Element id of the visible <label> (for listbox `aria-labelledby`). */
  optionsListLabelledBy,
  value,
  onChange,
  options,
  minWidth,
  maxWidth,
  placeholder = 'Select…'
}) {
  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [highlight, setHighlight] = useState(0);

  const selectedLabel = useMemo(() => {
    const hit = options.find((o) => String(o.value) === String(value));
    return hit?.label ?? placeholder;
  }, [options, value, placeholder]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return options;
    return options.filter((o) => String(o.label).toLowerCase().includes(t));
  }, [options, q]);

  useEffect(() => {
    if (open) {
      setQ('');
      setHighlight(0);
      const t = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    setHighlight((h) => {
      if (filtered.length === 0) return 0;
      return Math.min(h, filtered.length - 1);
    });
  }, [filtered.length]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = useCallback(
    (v) => {
      onChange(v);
      setOpen(false);
    },
    [onChange]
  );

  const onTriggerKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onSearchKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && filtered.length > 0) {
        e.preventDefault();
        const i = Math.min(highlight, filtered.length - 1);
        pick(filtered[i].value);
      }
    },
    [filtered, highlight, pick]
  );

  return (
    <Wrap ref={wrapRef} $minW={minWidth} $maxW={maxWidth}>
      <Trigger
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <TriggerText>{selectedLabel}</TriggerText>
        <FiChevronDown style={{ flexShrink: 0, opacity: 0.65 }} aria-hidden />
      </Trigger>
      {open && (
        <Panel role="listbox" aria-labelledby={optionsListLabelledBy || id}>
          <SearchInput
            ref={searchRef}
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setHighlight(0);
            }}
            placeholder="Type to search…"
            aria-label="Search options"
            onKeyDown={onSearchKeyDown}
          />
          <List>
            {filtered.length === 0 ? (
              <li style={{ padding: '0.75rem', color: '#94a3b8', fontSize: '0.85rem' }}>No matches</li>
            ) : (
              filtered.map((o, i) => {
                const selected = String(o.value) === String(value);
                return (
                  <OptionRow
                    key={String(o.value)}
                    role="option"
                    aria-selected={selected}
                    $highlight={i === highlight}
                    onMouseEnter={() => setHighlight(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(o.value)}
                  >
                    <SelectedMark aria-hidden>{selected ? '✓' : ''}</SelectedMark>
                    <span style={{ minWidth: 0 }}>{o.label}</span>
                  </OptionRow>
                );
              })
            )}
          </List>
        </Panel>
      )}
    </Wrap>
  );
}
